import { FloatingFocusManager, FloatingOverlay, FloatingPortal } from '@floating-ui/react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';
import { Button } from '../catalyst-ui-kit/button';
import { Input } from '../catalyst-ui-kit/input';
import { ScrollContainer } from '../ScrollContainer/ScrollContainer';
import { useMultiSelectContext } from './MultiSelectContext';
import { MultiSelectOption } from './MultiSelectOption';

function SearchInput() {
  const { searchPlaceholder, searchQuery, setSearchQuery, searchInputRef } =
    useMultiSelectContext();

  return (
    <div className="mb-0.5">
      <Input
        containerClassName="w-full border border-borderPrimary !bg-backgroundPrimary"
        leftElement={<MagnifyingGlassIcon className="size-4 text-contentSecondary" />}
        rightElement={
          <Button
            onClick={() => setSearchQuery('')}
            plain
            disabled={searchQuery === ''}
            className="-mr-1 size-5 cursor-pointer items-center justify-center !p-0.5 disabled:cursor-default"
          >
            <XMarkIcon className="size-4 text-contentSecondary" />
          </Button>
        }
        className="!p-0 text-sm"
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        aria-label="Search options"
        ref={searchInputRef}
      />
    </div>
  );
}

interface MultiSelectDropdownProps {
  children?: React.ReactNode;
}

export function MultiSelectDropdown({ children }: MultiSelectDropdownProps) {
  const {
    isOpen,
    disabled,
    multiselect,
    context,
    refs,
    showSearchInput,
    floatingStyles,
    searchInputRef,
    searchQuery,
    getFloatingProps,
    handleFloatingKeyDown,
    triggerLabelId,
    clearEnabled,
    handleClearSelection,
    clearButtonRef,
    maxHeight,
    filteredGroups,
    optionIndices,
    selectedValues,
    activeIndex,
    lastSelectedIndex,
    hoveredIndex,
    isShiftKeyDown,
    filteredOptions,
    setHoveredIndex,
  } = useMultiSelectContext();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (groupIndex: number) => {
    setCollapsedGroups((prev) => {
      const newCollapsedGroups = new Set(prev);
      if (newCollapsedGroups.has(groupIndex)) {
        newCollapsedGroups.delete(groupIndex);
      } else {
        newCollapsedGroups.add(groupIndex);
      }
      return newCollapsedGroups;
    });
  };

  if (!isOpen || disabled) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingOverlay className="z-50">
        <FloatingFocusManager
          context={context}
          initialFocus={showSearchInput ? searchInputRef : undefined}
          returnFocus={true}
        >
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              width: 'max-content',
              minWidth: refs.reference.current?.getBoundingClientRect().width,
            }}
            {...getFloatingProps({
              onKeyDown: handleFloatingKeyDown,
            })}
            id="select-options"
            role="listbox"
            aria-multiselectable={multiselect}
            aria-labelledby={triggerLabelId}
            tabIndex={0}
            className="z-50 max-w-xs select-none rounded-md border border-borderPrimary bg-backgroundTertiary shadow-lg outline-none"
          >
            <div className={twJoin('relative flex flex-col pb-0')}>
              {/* Fixed div */}
              {clearEnabled || showSearchInput ? (
                <div className="p-2 pb-0">
                  {clearEnabled && (
                    <button
                      className="group mb-2 flex w-full items-center gap-2 px-2 text-left text-sm font-semibold text-contentSecondary transition-colors hover:text-contentPrimary"
                      onClick={handleClearSelection}
                      ref={clearButtonRef}
                      aria-label="Clear selection"
                    >
                      <XMarkIcon className="size-4" />
                      <span>Clear Selection</span>
                    </button>
                  )}

                  {showSearchInput ? <SearchInput /> : null}
                </div>
              ) : null}

              {/* Scrolling div */}
              <ScrollContainer
                direction="vertical"
                className={cn('py-2', clearEnabled || showSearchInput ? 'pt-0' : '')}
                style={{ maxHeight: `${maxHeight}px` }}
                fadeSize={{ top: 0, bottom: 48 }}
              >
                {filteredGroups.map((group, groupIndex) => {
                  const isCollapsed = collapsedGroups.has(groupIndex);

                  return (
                    <div key={group.title ?? `group-${groupIndex}`}>
                      {/* Title */}
                      {group.title && (
                        <div
                          className={twJoin(
                            'flex cursor-pointer items-center justify-between px-2 py-1 text-2xs font-medium tracking-wide text-contentSecondary transition-colors hover:bg-backgroundHoverOverlay'
                          )}
                          onClick={() => toggleGroup(groupIndex)}
                          data-testid="group-title"
                        >
                          <span>{group.title}</span>
                          <ChevronDownIcon
                            className={twJoin(
                              'size-3 text-contentSecondary transition-transform',
                              !isCollapsed ? 'rotate-180' : ''
                            )}
                          />
                        </div>
                      )}
                      {/* Options */}
                      <motion.div
                        initial={false}
                        animate={{
                          height: isCollapsed ? 0 : 'auto',
                          opacity: isCollapsed ? 0 : 1,
                        }}
                        style={{ overflow: 'hidden' }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="px-2"
                      >
                        {group.options.map((option) => {
                          const currentIndex = optionIndices.get(option) ?? 0;
                          const isSelected = selectedValues.includes(option.value);

                          const isActive =
                            activeIndex === currentIndex ||
                            (searchQuery.length > 0 && activeIndex === null && currentIndex === 0);

                          const isInShiftRange =
                            multiselect &&
                            isShiftKeyDown &&
                            lastSelectedIndex !== null &&
                            hoveredIndex !== null &&
                            currentIndex >= Math.min(lastSelectedIndex, hoveredIndex) &&
                            currentIndex <= Math.max(lastSelectedIndex, hoveredIndex);

                          const shiftSelectAction =
                            multiselect &&
                            isShiftKeyDown &&
                            lastSelectedIndex !== null &&
                            hoveredIndex !== null
                              ? filteredOptions[hoveredIndex]?.value &&
                                selectedValues.includes(filteredOptions[hoveredIndex]?.value)
                                ? 'deselect'
                                : 'select'
                              : null;

                          return (
                            <MultiSelectOption
                              key={option.label ?? String(option.value)}
                              option={option}
                              currentIndex={currentIndex}
                              isSelected={isSelected}
                              isActive={isActive}
                              isInShiftRange={isInShiftRange}
                              shiftSelectAction={shiftSelectAction}
                              onMouseEnter={() => !option.disabled && setHoveredIndex(currentIndex)}
                              onMouseLeave={() => !option.disabled && setHoveredIndex(null)}
                            />
                          );
                        })}
                      </motion.div>
                    </div>
                  );
                })}

                {filteredOptions.length === 0 && (
                  <div className="p-2 text-center text-contentSecondary" role="status">
                    No items found
                  </div>
                )}

                {/* If children nodes have been passed on multiselect, display them here. */}
                {children}
              </ScrollContainer>
            </div>
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
